// ./src/components/features/Memberships/MembershipsList/MembershipsList.tsx
//
// Memberships list with a different presentation per breakpoint, switched live
// on window resize (useAppEnvironment is backed by useMediaQuery):
//
// - Desktop (>= md): a compact TABLE — type swatch · number · type · valid ·
//   created · row actions.
// - Tablet / mobile (< md): a CARD list.
//
// Interaction (best-practice, tap-safe):
// - Rows/cards are NEVER click-to-edit. Editing happens only via the explicit
//   Edit button, so an accidental tap can't open the form.
// - Desktop + tablet: Edit/Delete buttons are always visible.
// - Phones (< sm): swipe-to-reveal Edit/Delete; tapping the card does nothing.
//
// Each membership resolves its MembershipType (for the swatch color + name)
// from the optional MembershipTypesContext.
//
// Data resolves from <MembershipsProvider> when props are omitted.

import React, { useCallback, useMemo } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Box,
  Badge,
  ActionIcon,
  Tooltip,
  Skeleton,
  Table,
  Checkbox,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { STATUSES } from "@sdflc/utils";

import { Membership, getMembershipValidity } from "@/utils/api";
import { RowSelection } from "@/hooks";
import { useAppEnvironment } from "@/contexts";
import { useOptionalMembershipsContext } from "@/contexts/MembershipsContext/MembershipsContext";
import { useOptionalMembershipTypesContext } from "@/contexts/MembershipTypesContext/MembershipTypesContext";
import {
  AppIcon,
  ColorSwatch,
  EmptyState,
  SwipeableCard,
  SwipeAction,
  useSwipeableList,
  LoadMoreButton,
  CardSkeletonActions,
  CardSkeletonLines,
  CardSkeletonCheckbox,
  countCardActions,
  type CardSkeletonShape,
} from "@/components/generic";
import {
  useEntranceAnimation,
  entranceListVariants,
  entranceCardVariants,
} from "@/hooks/useEntranceAnimation";
import {
  formatSmartDateTime,
  formatDate,
  recordHasRight,
  HIGHLIGHT_STYLE,
  HIGHLIGHT_ROW_BG,
} from "@/utils";

// ============================================
// Types
// ============================================

export interface MembershipsListProps {
  /** Array of membership records. Optional when inside provider. */
  data?: Membership[];

  /** Loading state for initial load. Optional when inside provider. */
  loading?: boolean;

  /** Loading state for loading more items. Optional — reads from context. */
  loadingMore?: boolean;

  /** Whether there are more items to load. Optional — reads from context. */
  hasMore?: boolean;

  /** Callback to load more items. Optional — reads from context. */
  onLoadMore?: () => void;

  /** Callback when the Edit action is invoked (button or swipe). */
  onEditMembership?: (membership: Membership) => void;

  /** Callback when the Delete action is invoked (button or swipe). */
  onDeleteMembership?: (membership: Membership) => void;

  /** Predicate for whether a membership is being removed. Optional — reads from context. */
  isRemoving?: (id?: string) => boolean;

  /** ID of recently edited/added membership to highlight */
  highlightedId?: string | null;

  /** Callback when add button is clicked (for empty state) */
  onAddMembership?: () => void;

  /** Multi-row selection state. When provided (and enabled), rows are selectable. */
  selection?: RowSelection;

  /**
   * Whether selection controls are shown. On desktop/tablet this means checkboxes;
   * on phones the row is tap-to-toggle only while `selection.selectionMode` is on.
   */
  selectable?: boolean;
  /**
   * A search term or filter is narrowing the list. An empty result then means "no match" — NOT
   * "nothing here yet", so the first-run Add prompt would be telling somebody who just searched
   * that their records are gone. ⚠ The list cannot see the page's search box; this is a PROP.
   */
  hasQueryNarrowing?: boolean;
}

// ============================================
// Validity range formatting
// ============================================

// Subtle color for the Valid column, by derived validity. Active/perpetual stay
// muted; only the ones that need attention get color.
const VALIDITY_COLOR: Record<string, string> = {
  expired: "red.7",
  expiring_soon: "orange.7",
  active: "dimmed",
  perpetual: "dimmed",
};

const isDisabled = (membership: Membership): boolean =>
  membership.status === STATUSES.DISABLED;

const formatValidity = (
  membership: Membership,
  perpetualLabel: string,
  emptyLabel: string
): string => {
  const start = (membership.startsOn || "").trim();
  const end = (membership.expiresOn || "").trim();

  if (!start && !end) {
    return emptyLabel;
  }

  const startText = start ? formatDate(start) : emptyLabel;
  const endText = end ? formatDate(end) : perpetualLabel;

  return `${startText} → ${endText}`;
};

// ============================================
// Shared bits
// ============================================

// Always-visible row actions (desktop table + tablet cards). Explicit buttons
// are the ONLY way to edit/delete, so there's nothing to discover on hover.
const RowActions: React.FC<{
  membership: Membership;
  onEdit?: (membership: Membership) => void;
  onDelete?: (membership: Membership) => void;
  isDeleting: boolean;
}> = ({ membership, onEdit, onDelete, isDeleting }) => {
  const { t } = useTranslation(["common"]);

  // Per-record rights (from the API) gate the buttons on top of page-level rights.
  const showEdit = !!onEdit && recordHasRight(membership, "edit");
  const showDelete = !!onDelete && recordHasRight(membership, "remove");

  if (!showEdit && !showDelete) {
    return null;
  }

  return (
    <Group gap={4} wrap="nowrap">
      {showEdit && onEdit && (
        <Tooltip label={t("common:buttons.edit")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="blue"
            size="md"
            onClick={() => onEdit(membership)}
            disabled={isDeleting}
            aria-label={t("common:buttons.edit")}
          >
            <AppIcon iconName="Edit" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {showDelete && onDelete && (
        <Tooltip label={t("common:buttons.delete")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="red"
            size="md"
            onClick={() => onDelete(membership)}
            loading={isDeleting}
            disabled={isDeleting}
            aria-label={t("common:buttons.delete")}
          >
            <AppIcon iconName="Trash" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
};

// A small muted badge marking a disabled membership (kept minimal).
const DisabledBadge: React.FC = () => {
  const { t } = useTranslation(["common"]);

  return (
    <Badge size="xs" variant="light" color="gray">
      {t("common:status.disabled", { defaultValue: "Disabled" })}
    </Badge>
  );
};

// #281 — a membership bought with a test card (it admits only on events in test mode). As the gifts list's Test badge.
const TestBadge: React.FC = () => {
  const { t } = useTranslation(["memberships"]);

  return (
    <Badge size="sm" variant="light" color="yellow">
      {t("memberships:list.test", { defaultValue: "Test" })}
    </Badge>
  );
};

// ============================================
// Shared row shape
// ============================================

/** "3 of 10 used" for a pass with a usage limit, "3 used" without one, nothing for an unused unlimited one. */
const usesLabelOf = (used: number | null | undefined, max: number | null | undefined, t: (k: string, o?: any) => string): string | null => {
  const n = Number(used ?? 0);

  if (Number(max ?? 0) > 0) {
    return t("memberships:list.usesOf", { defaultValue: "{{used}} of {{max}} used", used: n, max: Number(max) });
  }

  return n > 0 ? t("memberships:list.uses", { defaultValue: "{{used}} used", used: n }) : null;
};

interface RowProps {
  membership: Membership;
  /** Resolved type name for display. */
  typeName: string;
  /** "3 of 10 used" / "3 used" — null when there is nothing to say. */
  usesLabel: string | null;
  /** Resolved type color for the swatch. */
  typeColor?: string | null;
  onEdit?: (membership: Membership) => void;
  onDelete?: (membership: Membership) => void;
  highlighted: boolean;
  isDeleting: boolean;
  /** i18n language for the localized timestamp. */
  language: string;
  /** Pre-formatted validity range. */
  validity: string;
  /** Mantine color for the validity text (expired/soon get attention). */
  validityColor: string;
  /** Whether the membership is disabled (row greyed + a badge). */
  disabled: boolean;
  /** Whether the desktop table has a leading selection column (list-level). */
  selectionColumn: boolean;
  /**
   * Whether THIS row can be selected — only if there's a bulk action the caller
   * can run on it (change → 'edit' right, or delete → 'remove' right). A row with
   * neither shows no checkbox and can't be toggled.
   */
  selectable: boolean;
  /** Whether this row is currently selected. */
  selected: boolean;
  /** Phones: selection mode is active (tap the card to toggle). */
  selectionMode: boolean;
  /** Toggle this row's selection (stable). */
  onToggleSelect?: (id: string) => void;
}

// ============================================
// Desktop: Table row (not clickable)
// ============================================

const MembershipTableRow: React.FC<RowProps> = ({
  membership,
  typeName,
  usesLabel,
  typeColor,
  onEdit,
  onDelete,
  highlighted,
  isDeleting,
  language,
  validity,
  validityColor,
  disabled,
  selectionColumn,
  selectable,
  selected,
  onToggleSelect,
}) => {
  return (
    <Table.Tr
      style={{
        opacity: isDeleting ? 0.5 : disabled ? 0.6 : 1,
        backgroundColor: highlighted
          ? HIGHLIGHT_ROW_BG
          : selected
          ? "var(--mantine-color-blue-light)"
          : undefined,
        transition: "background-color 150ms ease, opacity 150ms ease",
      }}
    >
      {selectionColumn && (
        <Table.Td style={{ width: 44 }}>
          {selectable && (
            <Checkbox
              checked={selected}
              onChange={() => membership.id && onToggleSelect?.(membership.id)}
              disabled={isDeleting}
              aria-label={membership.membershipNumber ?? undefined}
            />
          )}
        </Table.Td>
      )}
      <Table.Td style={{ width: 52 }}>
        <ColorSwatch color={typeColor ?? undefined} size={26} />
      </Table.Td>
      <Table.Td>
        <Group gap={6} wrap="nowrap">
          <Text size="md" fw={700} lineClamp={1}>
            {membership.membershipNumber || "—"}
          </Text>
          {disabled && <DisabledBadge />}
          {membership.isTest && <TestBadge />}
        </Group>
        {membership.personGroup?.name && (
          <Text size="md" c="dimmed" lineClamp={1}>
            {membership.personGroup.name}
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        <Text size="md" lineClamp={1}>
          {typeName}
        </Text>
        {usesLabel && (
          <Text size="md" c="dimmed" lineClamp={1}>
            {usesLabel}
          </Text>
        )}
      </Table.Td>
      <Table.Td style={{ width: 220 }}>
        <Text size="md" c={validityColor} lineClamp={1}>
          {validity}
        </Text>
      </Table.Td>
      <Table.Td style={{ width: 210 }}>
        <Text size="sm" c="dimmed">
          {formatSmartDateTime(membership.createdAt, language)}
        </Text>
      </Table.Td>
      <Table.Td style={{ width: 96 }}>
        <RowActions
          membership={membership}
          onEdit={onEdit}
          onDelete={onDelete}
          isDeleting={isDeleting}
        />
      </Table.Td>
    </Table.Tr>
  );
};

// A skeleton row that IS a real Table.Tr — identical column widths, spacing and
// element sizes to MembershipTableRow, so the loading table matches the loaded one.
const MembershipTableRowSkeleton: React.FC<{ selectionColumn?: boolean }> = ({
  selectionColumn,
}) => (
  <Table.Tr>
    {selectionColumn && (
      <Table.Td style={{ width: 44 }}>
        <Skeleton height={20} width={20} radius="sm" />
      </Table.Td>
    )}
    <Table.Td style={{ width: 52 }}>
      <Skeleton height={26} width={26} radius="sm" />
    </Table.Td>
    <Table.Td>
      <Skeleton height={16} width="50%" maw={160} />
    </Table.Td>
    <Table.Td>
      <Skeleton height={16} width="60%" maw={200} />
    </Table.Td>
    <Table.Td style={{ width: 220 }}>
      <Skeleton height={16} width={170} />
    </Table.Td>
    <Table.Td style={{ width: 210 }}>
      <Skeleton height={16} width={150} />
    </Table.Td>
    <Table.Td style={{ width: 96 }}>
      <Group gap={4} wrap="nowrap">
        <Skeleton circle height={28} width={28} />
        <Skeleton circle height={28} width={28} />
      </Group>
    </Table.Td>
  </Table.Tr>
);

// ============================================
// Mobile / tablet: Card (not clickable)
// ============================================

interface CardProps extends RowProps {
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const MembershipCardComponent: React.FC<CardProps> = ({
  membership,
  typeName,
  usesLabel,
  typeColor,
  onEdit,
  onDelete,
  highlighted,
  isDeleting,
  validity,
  validityColor,
  disabled,
  selectable,
  selected,
  selectionMode,
  onToggleSelect,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
}) => {
  const { t } = useTranslation(["common"]);

  const toggleSelect = () => membership.id && onToggleSelect?.(membership.id);

  const swipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];

    if (onEdit && recordHasRight(membership, "edit")) {
      actions.push({
        key: "edit",
        icon: "Edit",
        label: t("common:buttons.edit"),
        color: "blue",
        onClick: () => onEdit(membership),
        disabled: isDeleting,
      });
    }

    if (onDelete && recordHasRight(membership, "remove")) {
      actions.push({
        key: "delete",
        icon: "Trash",
        label: t("common:buttons.delete"),
        color: "red",
        onClick: () => onDelete(membership),
        loading: isDeleting,
        disabled: isDeleting,
      });
    }

    return actions;
  }, [onEdit, onDelete, membership, isDeleting, t]);

  // showActions=false on phones (swipe reveals them); true on tablet.
  // showCheckbox renders a leading selection checkbox.
  const cardBody = (showActions: boolean, showCheckbox: boolean) => (
    <Paper
      p="md"
      withBorder
      radius="md"
      opacity={isDeleting ? 0.5 : disabled ? 0.6 : 1}
      style={{
        transition: "opacity 150ms ease, box-shadow 150ms ease",
        backgroundColor: selected ? "var(--mantine-color-blue-light)" : undefined,
        ...(highlighted ? HIGHLIGHT_STYLE : {}),
      }}
    >
      <Group gap="md" wrap="nowrap" align="center">
        {showCheckbox && (
          <Checkbox
            checked={selected}
            onChange={toggleSelect}
            onClick={(e) => e.stopPropagation()}
            disabled={isDeleting}
            aria-label={membership.membershipNumber ?? undefined}
          />
        )}
        <ColorSwatch color={typeColor ?? undefined} />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group gap={6} wrap="nowrap">
            <Text size="md" fw={700} lineClamp={1}>
              {membership.membershipNumber || "—"}
            </Text>
            {disabled && <DisabledBadge />}
            {membership.isTest && <TestBadge />}
          </Group>
          {membership.personGroup?.name && (
            <Text size="md" lineClamp={1}>
              {membership.personGroup.name}
            </Text>
          )}
          <Text size="md" lineClamp={1}>
            {typeName}
          </Text>
          {usesLabel && (
            <Text size="md" c="dimmed" lineClamp={1}>
              {usesLabel}
            </Text>
          )}
          <Text size="md" c={validityColor} lineClamp={1}>
            {validity}
          </Text>
        </Box>
        {showActions && (
          <RowActions
            membership={membership}
            onEdit={onEdit}
            onDelete={onDelete}
            isDeleting={isDeleting}
          />
        )}
      </Group>
    </Paper>
  );

  // Selection mode (entered on phones): a selectable card is a tap-to-toggle
  // target with a leading checkbox; a non-selectable one (no edit/remove right)
  // shows neither and can't be toggled. Tablet keeps its checkbox + action
  // buttons (a stray card tap shouldn't toggle there).
  if (selectionMode) {
    return (
      <>
        <Box
          hiddenFrom="sm"
          onClick={selectable ? toggleSelect : undefined}
          style={{ cursor: selectable ? "pointer" : "default", userSelect: "none" }}
        >
          {cardBody(false, selectable)}
        </Box>
        <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
      </>
    );
  }

  // Not in selection mode: phones NEVER show a checkbox (selection is a mode on
  // phones) — they swipe to reveal actions, or show a plain card when there are
  // none. Tablet shows action buttons + a checkbox for selectable rows.
  return (
    <>
      <Box hiddenFrom="sm">
        {swipeActions.length > 0 ? (
          <SwipeableCard
            leftSwipeActions={swipeActions}
            forceClose={forceClose}
            onForceCloseComplete={onForceCloseComplete}
            onSwipeStateChange={onSwipeStateChange}
            disabled={isDeleting}
            borderRadius="var(--mantine-radius-md)"
          >
            {cardBody(false, false)}
          </SwipeableCard>
        ) : (
          cardBody(false, false)
        )}
      </Box>
      <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
    </>
  );
};

const MembershipCard = React.memo(MembershipCardComponent);

// A skeleton card matching MembershipCardComponent's cardBody: same padding, 32px
// swatch, three-line block, and tablet-only action buttons.
const MembershipCardSkeleton: React.FC<CardSkeletonShape> = ({ actions, withCheckbox }) => (
  <Paper p="md" withBorder radius="md">
    <Group gap="md" wrap="nowrap" align="center">
      <CardSkeletonCheckbox show={withCheckbox} />
      <Skeleton height={32} width={32} radius="sm" />
      <CardSkeletonLines count={3} gap={0} />
      <CardSkeletonActions count={actions} />
    </Group>
  </Paper>
);

// ============================================
// Main Component
// ============================================

const MembershipsListComponent: React.FC<MembershipsListProps> = ({
  data: dataProp,
  loading: loadingProp,
  loadingMore: loadingMoreProp,
  hasMore: hasMoreProp,
  onLoadMore: onLoadMoreProp,
  onEditMembership,
  onDeleteMembership,
  isRemoving: isRemovingProp,
  highlightedId,
  onAddMembership,
  selection,
  selectable = false,
  hasQueryNarrowing = false,
}) => {
  const { t, i18n } = useTranslation(["memberships", "common"]);
  const { isDesktop } = useAppEnvironment();
  const language = i18n.language;

  const isSelectable = selectable && !!selection;
  // The tablet card's row buttons, one per handler — what the card skeleton draws (▶ check-card-skeletons).
  const cardActions = countCardActions(onEditMembership, onDeleteMembership);

  // ── Context vs Props resolution ──
  const membershipsContext = useOptionalMembershipsContext();
  const typesCtx = useOptionalMembershipTypesContext();

  const memberships = dataProp ?? membershipsContext?.memberships ?? [];
  const loading = loadingProp ?? membershipsContext?.loading ?? false;
  const loadingMore = loadingMoreProp ?? membershipsContext?.loadingMore ?? false;
  const hasMore = hasMoreProp ?? membershipsContext?.hasMore ?? false;
  const onLoadMore = onLoadMoreProp ?? membershipsContext?.loadMore;
  const isRemoving =
    isRemovingProp ?? membershipsContext?.isRemoving ?? (() => false);

  const {
    openCard,
    closeAllCards,
    shouldForceClose,
    handleForceCloseComplete,
  } = useSwipeableList();

  const { hasAnimated } = useEntranceAnimation({ itemCount: memberships.length });

  const perpetualLabel = t("memberships:list.perpetual", {
    defaultValue: "perpetual",
  });
  const emptyValidityLabel = t("memberships:list.noDates", {
    defaultValue: "—",
  });

  // ── Wrapped handlers (close any open swipe before acting) ──
  const handleEdit = useCallback(
    (membership: Membership) => {
      closeAllCards();
      onEditMembership?.(membership);
    },
    [onEditMembership, closeAllCards]
  );

  const handleDelete = useCallback(
    (membership: Membership) => {
      closeAllCards();
      onDeleteMembership?.(membership);
    },
    [onDeleteMembership, closeAllCards]
  );

  const handleSwipeStateChange = useCallback(
    (membershipId: string, isOpen: boolean) => {
      if (isOpen) {
        openCard(membershipId);
      }
    },
    [openCard]
  );

  // ⚠ A SKELETON IS FOR THE FIRST READ ONLY. `loading && x.length === 0` re-shows it on every
  // refetch of an EMPTY list, so an empty page reads skeleton → "none" → skeleton → "none". Once
  // the first read is back, a refetch keeps the rows OR the empty state and ContentLoadingOverlay
  // says it is reloading. ⚠ The fallback answers "is this all the data there will ever be?" for a
  // caller with no provider. ▶ npm run check-list-states
  const isLoaded = membershipsContext?.isLoaded ?? dataProp !== undefined;
  const showSkeleton = !isLoaded;

  // ── Empty ──
  if (!showSkeleton && memberships.length === 0) {
    if (hasQueryNarrowing) {
      return (
        <Text size="md" c="dimmed" ta="center" p="md">
          {t("memberships:emptyState.noMatch", { defaultValue: "No memberships match your search or filters." })}
        </Text>
      );
    }

    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title={t("memberships:emptyState.title", {
            defaultValue: "No memberships yet",
          })}
          description={t("memberships:emptyState.description", {
            defaultValue: "Issue a membership pass to get started.",
          })}
          iconName="Id"
          actionLabel={t("memberships:emptyState.actionLabel", {
            defaultValue: "Add membership",
          })}
          onAction={onAddMembership}
          size="md"
        />
      </Paper>
    );
  }

  // A row is selectable only if there's a bulk action the caller can actually run
  // on it: change (row edit callback present + 'edit' right) or delete (row delete
  // callback present + 'remove' right). Selection exists for bulk update/removal,
  // so a record that supports neither is never selectable.
  const rowIsSelectable = (membership: Membership): boolean =>
    isSelectable &&
    ((!!onEditMembership && recordHasRight(membership, "edit")) ||
      (!!onDeleteMembership && recordHasRight(membership, "remove")));

  const rowProps = (membership: Membership): RowProps => {
    const type = typesCtx?.getById(membership.membershipTypeId ?? undefined);

    return {
      membership,
      typeName: type?.name ?? "—",
      usesLabel: usesLabelOf(membership.usesCount, Number(type?.maxUsageCount ?? 0) > 0 ? Number(type?.maxUsageCount) + Number(membership.extraUses ?? 0) : null, t),
      typeColor: type?.color,
      onEdit: onEditMembership ? handleEdit : undefined,
      onDelete: onDeleteMembership ? handleDelete : undefined,
      highlighted: membership.id === highlightedId,
      isDeleting: isRemoving(membership.id),
      language,
      validity: formatValidity(membership, perpetualLabel, emptyValidityLabel),
      validityColor: VALIDITY_COLOR[getMembershipValidity(membership.expiresOn)] ?? "dimmed",
      disabled: isDisabled(membership),
      selectionColumn: isSelectable,
      selectable: rowIsSelectable(membership),
      selected: selection?.isSelected(membership.id) ?? false,
      selectionMode: selection?.selectionMode ?? false,
      onToggleSelect: selection?.toggle,
    };
  };

  // The "select all" checkbox operates only on the selectable loaded rows.
  const selectableVisibleIds = memberships
    .filter((m) => rowIsSelectable(m))
    .map((m) => m.id)
    .filter((id): id is string => !!id);

  const loadMore = (
    <LoadMoreButton
      hasMore={hasMore}
      loading={loading}
      loadingMore={loadingMore}
      onLoadMore={onLoadMore}
    />
  );

  // ── Desktop: table ──
  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                {isSelectable && (
                  <Table.Th style={{ width: 44 }}>
                    <Checkbox
                      checked={selection!.allSelected(selectableVisibleIds)}
                      indeterminate={selection!.someSelected(selectableVisibleIds)}
                      onChange={() => selection!.toggleAll(selectableVisibleIds)}
                      disabled={selectableVisibleIds.length === 0}
                      aria-label={t("common:selection.selectAll", {
                        defaultValue: "Select all",
                      })}
                    />
                  </Table.Th>
                )}
                <Table.Th style={{ width: 52 }} />
                <Table.Th>
                  {t("memberships:list.columns.number", {
                    defaultValue: "Number",
                  })}
                </Table.Th>
                <Table.Th>
                  {t("memberships:list.columns.type", { defaultValue: "Type" })}
                </Table.Th>
                <Table.Th style={{ width: 220 }}>
                  {t("memberships:list.columns.valid", {
                    defaultValue: "Valid",
                  })}
                </Table.Th>
                <Table.Th style={{ width: 210 }}>
                  {t("memberships:list.columns.createdAt", {
                    defaultValue: "Created",
                  })}
                </Table.Th>
                <Table.Th style={{ width: 96 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {showSkeleton
                ? Array.from({ length: 6 }).map((_, i) => (
                    <MembershipTableRowSkeleton key={i} selectionColumn={isSelectable} />
                  ))
                : memberships.map((membership) => (
                    <MembershipTableRow
                      key={membership.id}
                      {...rowProps(membership)}
                    />
                  ))}
            </Table.Tbody>
          </Table>
        </Paper>
        {!showSkeleton && loadMore}
      </Stack>
    );
  }

  // ── Tablet / mobile: cards ──
  if (showSkeleton) {
    return (
      <Stack gap="sm">
        {Array.from({ length: 5 }).map((_, i) => (
          <MembershipCardSkeleton key={i} actions={cardActions} withCheckbox={isSelectable} />
        ))}
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <motion.div
        variants={hasAnimated ? undefined : entranceListVariants}
        initial={hasAnimated ? false : "hidden"}
        animate={hasAnimated ? undefined : "visible"}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--mantine-spacing-sm)",
        }}
      >
        <AnimatePresence mode="sync">
          {memberships.map((membership) => (
            <motion.div
              key={membership.id}
              variants={hasAnimated ? undefined : entranceCardVariants}
              initial={hasAnimated ? false : "hidden"}
              animate={hasAnimated ? undefined : "visible"}
              exit="exit"
              layout
            >
              <MembershipCard
                {...rowProps(membership)}
                forceClose={shouldForceClose(membership.id || "")}
                onForceCloseComplete={() =>
                  handleForceCloseComplete(membership.id || "")
                }
                onSwipeStateChange={(isOpen) =>
                  handleSwipeStateChange(membership.id || "", isOpen)
                }
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
      {loadMore}
    </Stack>
  );
};

export const MembershipsList = React.memo(MembershipsListComponent);
